// feat/allocate-fee-earner-button (19 May 2026)
//
// Inline "Allocate to fee earner" action shown on rows that are
// dead-ended by a missing data.responsible_fee_earner. Open to every
// authenticated team member per Vanessa's instruction — this is the
// user-facing complement to PR #482 (the dry-run backfill script).
//
// Permissions: any authenticated user can see + click. NOT gated to
// admin/director. Visibility is decided by the parent — render this
// only on rows where the responsible_fee_earner is missing/empty.
import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { UserPlus } from "lucide-react";
import { errorMessage } from "../utils/errorMessage";
import { Button } from "./ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "./ui/dialog";
import { API } from "../config/api";

export default function AllocateFeeEarnerButton({
  submissionId,
  onAllocated,
  buttonLabel = "Allocate to fee earner",
  buttonSize = "sm",
  buttonVariant = "outline",
}) {
  const [open, setOpen] = useState(false);
  const [options, setOptions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selected, setSelected] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const handleOpen = async () => {
    setSelected("");
    setOpen(true);
    if (options.length === 0) {
      setLoading(true);
      try {
        const res = await axios.get(`${API}/lookup/fee-earners`);
        // /api/lookup/fee-earners returns [{name, email, position}, ...]
        // already filtered to exclude former staff.
        setOptions(Array.isArray(res?.data) ? res.data : []);
      } catch (err) {
        const fallback = "Failed to load fee earner list. Please try again.";
        const msg = errorMessage(err, fallback);
        toast.error(msg);
        setOpen(false);
      } finally {
        setLoading(false);
      }
    }
  };

  const handleSubmit = async () => {
    const name = (selected || "").trim();
    if (!name) {
      toast.error("Pick a fee earner from the list");
      return;
    }
    if (!submissionId) {
      toast.error("Submission id missing — refresh the page and try again");
      return;
    }
    setSubmitting(true);
    try {
      const res = await axios.post(
        `${API}/matters/submission/${encodeURIComponent(submissionId)}/allocate-fee-earner`,
        { fee_earner: name },
      );
      if (res?.data?.success) {
        const newFE = res.data.fee_earner?.name || name;
        const prev = res.data.previous_fee_earner || "";
        toast.success(
          prev
            ? `Fee earner reallocated: ${prev} → ${newFE}`
            : `Fee earner allocated: ${newFE}`,
        );
        setOpen(false);
        if (typeof onAllocated === "function") {
          onAllocated(res.data);
        }
      } else {
        toast.error("Allocate Fee Earner returned an unexpected response");
      }
    } catch (err) {
      const fallback = "Failed to allocate fee earner. Please try again.";
      const msg = errorMessage(err, fallback);
      toast.error(msg);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <>
      <Button
        type="button"
        size={buttonSize}
        variant={buttonVariant}
        onClick={handleOpen}
        data-testid="allocate-fe-button"
      >
        <UserPlus className="w-4 h-4 mr-2" />
        {buttonLabel}
      </Button>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-md" data-testid="allocate-fe-modal">
          <DialogHeader>
            <DialogTitle>Allocate fee earner</DialogTitle>
            <DialogDescription>
              Pick the responsible fee earner for this matter. They will
              receive a notification and the row will unblock for
              invoice / post-intake actions.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-3 py-2">
            {loading ? (
              <p className="text-sm text-slate-500">Loading fee earners…</p>
            ) : (
              <select
                data-testid="allocate-fe-dropdown"
                value={selected}
                onChange={(e) => setSelected(e.target.value)}
                disabled={submitting}
                className="w-full rounded-md border border-slate-300 bg-white px-3 py-2 text-sm"
              >
                <option value="">— pick a fee earner —</option>
                {options.map((o) => (
                  <option key={o.email || o.name} value={o.name}>
                    {o.name}
                    {o.position ? ` · ${o.position}` : ""}
                  </option>
                ))}
              </select>
            )}
          </div>

          <DialogFooter>
            <Button
              type="button"
              variant="ghost"
              onClick={() => setOpen(false)}
              disabled={submitting}
              data-testid="allocate-fe-cancel"
            >
              Cancel
            </Button>
            <Button
              type="button"
              onClick={handleSubmit}
              disabled={submitting || loading || !selected}
              data-testid="allocate-fe-submit"
            >
              {submitting ? "Allocating…" : "Allocate"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
